import React, { useMemo, useState } from "react";
import { Layout, Alert } from "antd";
import AppHeader from "../../components/AppHeader/AppHeader";
import FiltersBar from "../../components/FiltersBar/FiltersBar";
import DashboardSkeleton from "../../components/Skeleton/DashboardSkeleton";
import { useUserIssues } from "../../hooks/useUserIssues";
import { ALL_USERS_VALUE } from "../../constants/gitlab";
import {
  collectFilterOptions,
  computeIssueStats,
  filterIssues,
} from "../../utils/issue";
import UserIssueCard from "./UserIssueCard";

const { Content } = Layout;

const IssuesDashboard = () => {
  const {
    gitlabUsers,
    userDataMap,
    isLoadingUsers,
    isLoading,
    isBackgroundRefreshing,
    countdown,
    refreshAll,
    refreshUser,
  } = useUserIssues();

  const [selectedUser, setSelectedUser] = useState(ALL_USERS_VALUE);
  const [search, setSearch] = useState("");
  const [labelFilter, setLabelFilter] = useState("all");
  const [milestoneFilter, setMilestoneFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const visibleUsernames = useMemo(() => {
    if (selectedUser === ALL_USERS_VALUE) {
      return Object.keys(userDataMap);
    }
    return [selectedUser].filter((user) => userDataMap[user]);
  }, [selectedUser, userDataMap]);

  const allVisibleIssues = useMemo(() => {
    return visibleUsernames.flatMap(
      (username) => userDataMap[username]?.issues || []
    );
  }, [visibleUsernames, userDataMap]);

  const filterOptions = useMemo(
    () => collectFilterOptions(allVisibleIssues),
    [allVisibleIssues]
  );

  const summaryStats = useMemo(
    () =>
      computeIssueStats(
        filterIssues(allVisibleIssues, {
          search,
          label: labelFilter,
          milestone: milestoneFilter,
          status: statusFilter,
        })
      ),
    [allVisibleIssues, search, labelFilter, milestoneFilter, statusFilter]
  );

  const filters = {
    search,
    label: labelFilter,
    milestone: milestoneFilter,
    status: statusFilter,
  };

  return (
    <Layout className="dashboard-layout">
      <div className="dashboard-sticky">
        <AppHeader
          countdown={countdown}
          isLoading={isLoading}
          isBackgroundRefreshing={isBackgroundRefreshing}
          onRefresh={refreshAll}
          stats={summaryStats}
        />

        <FiltersBar
          gitlabUsers={gitlabUsers}
          selectedUser={selectedUser}
          onUserChange={setSelectedUser}
          isLoadingUsers={isLoadingUsers}
          search={search}
          onSearchChange={setSearch}
          labelFilter={labelFilter}
          onLabelChange={setLabelFilter}
          milestoneFilter={milestoneFilter}
          onMilestoneChange={setMilestoneFilter}
          statusFilter={statusFilter}
          onStatusChange={setStatusFilter}
          labelOptions={filterOptions.labels}
          milestoneOptions={filterOptions.milestones}
        />
      </div>

      <Content className="dashboard-content">
        {isBackgroundRefreshing && (
          <Alert
            type="info"
            showIcon
            banner
            message="Refreshing data in background..."
            className="background-refresh-alert"
          />
        )}

        {isLoading && visibleUsernames.length === 0 ? (
          <DashboardSkeleton count={3} />
        ) : (
          <div className="user-cards">
            {visibleUsernames.map((username) => {
              const user = gitlabUsers.find((u) => u.username === username);
              const userData = userDataMap[username];
              const issues = filterIssues(userData?.issues || [], filters);

              return (
                <UserIssueCard
                  key={username}
                  username={username}
                  displayName={user?.name}
                  issues={issues}
                  userData={userData}
                  onRefreshUser={refreshUser}
                />
              );
            })}
          </div>
        )}
      </Content>
    </Layout>
  );
};

export default IssuesDashboard;
